import { forwardRef } from 'react'

import { Cross2Icon } from '@radix-ui/react-icons'
import * as ToastPrimitives from '@radix-ui/react-toast'
import { type VariantProps, cva } from 'class-variance-authority'

import { cn } from '../utils'

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    className={cn(
      `fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto
      sm:flex-col md:max-w-[420px]`,
      className
    )}
    ref={ref}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-xl border-0.5 py-3 px-4 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
  {
    defaultVariants: {
      variant: 'default',
    },
    variants: {
      variant: {
        default:
          'bg-background text-foreground border-border backdrop-bright-75 backdrop-contrast-75 backdrop-saturate-200',
      },
    },
  }
)

const Toast = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      className={cn(toastVariants({ variant }), className)}
      ref={ref}
      {...props}
    />
  )
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    className={cn(
      `focus:ring-ring group-[.destructive]:hover:text-destructive-foreground
      group-[.destructive]:border-muted/40 hover:bg-secondary
      group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive
      group-[.destructive]:focus:ring-destructive inline-flex h-8 shrink-0 items-center justify-center
      rounded-md border bg-transparent px-3 font-medium transition-colors focus:outline-none focus:ring-1
      disabled:pointer-events-none disabled:opacity-50`,
      className
    )}
    ref={ref}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    className={cn(
      `text-muted-foreground hover:text-foreground group-[.success]:text-success/20
      group-[.success]:hover:text-success/10 absolute right-1 top-1 rounded-md p-1 opacity-0
      transition-opacity focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100
      group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50
      group-[.destructive]:focus:ring-red-400 group-[.success]:focus:ring-green-400
      group-[.destructive]:focus:ring-offset-red-600 group-[.success]:focus:ring-offset-green-600`,
      className
    )}
    ref={ref}
    toast-close=""
    {...props}
  >
    <Cross2Icon className="h-4 w-4" />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title className={cn('[&+div]:text-sm', className)} ref={ref} {...props} />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description className={cn('opacity-70', className)} ref={ref} {...props} />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type TToastProps = React.ComponentPropsWithoutRef<typeof Toast>

type TToastActionElement = React.ReactElement<typeof ToastAction>

export {
  type TToastActionElement,
  type TToastProps,
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
}
